import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import type { PersonnelProject, RateSchedule, TMTimesheet } from "@/hooks/usePersonnelProjects";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

interface Props {
  project: PersonnelProject;
  rates: RateSchedule[];
  timesheets: TMTimesheet[];
}

export function PersonnelOverviewTab({ project, rates, timesheets }: Props) {
  const isMobile = useIsMobile();
  const totalBilled = timesheets.reduce((s, t) => s + (t.total_billed || 0), 0);
  const burnPct = project.nte_amount && project.nte_amount > 0 ? (totalBilled / project.nte_amount) * 100 : null;

  const weeklyAvg = timesheets.length > 0 ? totalBilled / timesheets.length : 0;
  const weeksToNte = project.nte_amount && weeklyAvg > 0 ? (project.nte_amount - totalBilled) / weeklyAvg : null;

  const activeRates = rates.filter((r) => r.is_active);

  return (
    <div className="space-y-6 mt-4">
      {/* NTE Burn Rate */}
      {project.nte_amount && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">NTE Burn Rate</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Contract NTE: {fmt(project.nte_amount)}</span>
              <span>{fmt(totalBilled)} / {fmt(project.nte_amount)} ({burnPct?.toFixed(1)}%)</span>
            </div>
            <Progress value={Math.min(burnPct ?? 0, 100)} className="h-3" />
            {weeklyAvg > 0 && (
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Weekly avg billing: {fmt(weeklyAvg)}</p>
                {weeksToNte !== null && <p>Weeks to NTE: {weeksToNte.toFixed(1)} weeks</p>}
                {burnPct !== null && burnPct >= 80 && (
                  <p className="text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Approaching NTE limit
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Role Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Role Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {isMobile ? (
            <div className="space-y-3">
              {activeRates.map((r) => {
                const margin = r.bill_rate_regular > 0
                  ? ((r.bill_rate_regular - r.cost_rate) / r.bill_rate_regular * 100)
                  : 0;
                return (
                  <div key={r.id} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{r.role_name}</span>
                      <span className={`font-medium ${margin >= 25 ? "text-emerald-600" : margin >= 15 ? "text-amber-600" : "text-destructive"}`}>
                        {margin.toFixed(0)}%
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 text-sm text-muted-foreground">
                      <span>Workers: {r.worker_count}</span>
                      <span>Bill: ${r.bill_rate_regular}/HR</span>
                      <span>OT: ${r.bill_rate_ot}/HR</span>
                      <span>Cost: ${r.cost_rate}/HR</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Workers</TableHead>
                  <TableHead className="text-right">Bill Rate</TableHead>
                  <TableHead className="text-right">OT Rate</TableHead>
                  <TableHead className="text-right">Cost Rate</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeRates.map((r) => {
                  const margin = r.bill_rate_regular > 0
                    ? ((r.bill_rate_regular - r.cost_rate) / r.bill_rate_regular * 100)
                    : 0;
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.role_name}</TableCell>
                      <TableCell className="text-right">{r.worker_count}</TableCell>
                      <TableCell className="text-right">${r.bill_rate_regular}/HR</TableCell>
                      <TableCell className="text-right">${r.bill_rate_ot}/HR</TableCell>
                      <TableCell className="text-right">${r.cost_rate}/HR</TableCell>
                      <TableCell className={`text-right font-medium ${margin >= 25 ? "text-emerald-600" : margin >= 15 ? "text-amber-600" : "text-destructive"}`}>
                        {margin.toFixed(0)}%
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
